'use client'

import { useState } from 'react'
import { useHydrated } from './useHydrated'

/**
 * Tasti di condivisione per la pagina articolo. Isomorfo con <ArticleView> (serve sia
 * l'articolo SSR sia quelli caricati dallo scroll infinito): i target social sono <a>
 * puri — funzionano anche prima dell'idratazione / senza JS. «Copia link» e la
 * condivisione nativa (Web Share API) richiedono JS e compaiono solo dopo l'idratazione.
 *
 * `url` è l'URL ASSOLUTO canonico dell'articolo (passato dal server, per-tenant), così
 * gli intent social puntano sempre al permalink corretto anche nello stream client-side.
 */
const ICON =
  'inline-flex size-9 items-center justify-center rounded-full text-zinc-500 ring-1 ring-zinc-200 transition-colors hover:bg-accent-soft hover:text-accent-strong hover:ring-accent/30 focus-visible:outline-none'

export function ShareButtons({ url, title, className = '' }: { url: string; title: string; className?: string }) {
  const [copied, setCopied] = useState(false)

  // Web Share API e clipboard esistono solo lato client → gate post-idratazione. Il valore si
  // CALCOLA quando il client c'è: non c'è niente da conservare in stato.
  const hydrated = useHydrated()
  const canNativeShare = hydrated && typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const u = encodeURIComponent(url)
  const t = encodeURIComponent(title)

  const targets = [
    {
      label: 'Condividi su Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    },
    {
      label: 'Condividi su X',
      href: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
      path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    },
    {
      label: 'Condividi su LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
    },
    {
      label: 'Condividi su WhatsApp',
      href: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} - ${url}`)}`,
      path: 'M20.52 3.48A11.86 11.86 0 0012 0C5.373 0 0 5.373 0 12c0 2.117.553 4.088 1.603 5.84L0 24l6.41-1.67A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12 0-3.188-1.244-6.169-3.48-8.52zM12 21.5c-1.54 0-3.05-.39-4.36-1.12l-.31-.17-3.8.99.99-3.7-.19-.31A9.5 9.5 0 012.5 12 9.5 9.5 0 0112 2.5 9.5 9.5 0 0121.5 12 9.5 9.5 0 0112 21.5z',
    },
    {
      label: 'Condividi su Telegram',
      href: `https://t.me/share/url?url=${u}&text=${t}`,
      path: 'M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12c6.627 0 12-5.373 12-12S18.627 0 12 0zm5.04 8.64l-1.86 8.64c-.14.64-.5.8-1.02.5l-2.82-2.08-1.36 1.31c-.15.15-.28.28-.57.28l.2-2.86 5.2-4.7c.23-.2-.05-.32-.36-.12L7.6 12.62 4.56 11.5c-.62-.2-.63-.62.13-.92L17.4 6.24c.6-.18 1.12.14.64 2.4z',
    },
  ]

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard negata: nessun feedback, i link social restano disponibili */
    }
  }

  const nativeShare = () => {
    void navigator.share?.({ title, url }).catch(() => {})
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-zinc-600">Condividi</span>

      {targets.map((s) => (
        <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className={ICON}>
          <svg className="size-[18px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden focusable="false">
            <path d={s.path} />
          </svg>
        </a>
      ))}

      <button type="button" onClick={copy} aria-label="Copia link" className={ICON}>
        <svg className="size-[18px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden focusable="false">
          <path d="M16 1H8a2 2 0 00-2 2v2H5a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2v-2h2a2 2 0 002-2V5l-5-4zM14 21H5V7h1v9a2 2 0 002 2h6v3zm4-5h-2V7a2 2 0 00-2-2H8V3h8l2 2v11z" />
        </svg>
      </button>

      {canNativeShare ? (
        <button type="button" onClick={nativeShare} aria-label="Condividi" className={ICON}>
          <svg className="size-[18px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden focusable="false">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77l-7.02-4.11a3 3 0 000-1.48l7.02-4.11A3 3 0 0018 7.92a3.08 3.08 0 10-3.08-3.08c0 .24.04.47.09.7L7.99 9.65A3.08 3.08 0 005 9a3.08 3.08 0 100 6c.76 0 1.44-.3 1.96-.77l7.02 4.11c-.05.23-.09.46-.09.7A3.08 3.08 0 1018 16.08z" />
          </svg>
        </button>
      ) : null}

      <span aria-live="polite" className={copied ? 'text-xs font-medium text-accent-strong' : 'sr-only'}>
        {copied ? 'Link copiato!' : ''}
      </span>
    </div>
  )
}
